import {
  UUID_CHAR_WIFI_ENC_METHOD,
  UUID_CHAR_WIFI_PASSWORD,
  UUID_CHAR_WIFI_SSID,
  UUID_WIFI_SERVICE,
} from "hooks/useBle";
import { trackEvent } from "utils/analytics";
import { notify } from "utils/notify";

interface WizardBleHandlersArgs {
  scanForLifeguard: () => Promise<BluetoothDevice | null>;
  connectToDevice: (device: BluetoothDevice) => Promise<unknown>;
  sendWifiCredentials: (
    gatt: BluetoothRemoteGATTServer,
    service: string,
    ssidChar: string,
    passwordChar: string,
    encChar: string,
    ssid: string,
    password: string,
  ) => Promise<void>;
  connectedDevice: BluetoothDevice | null | undefined;
  deviceDisplayName?: string;
  isConnected: boolean;
  wifiSSID: string;
  wifiPassword: string;
  setSkipScan: (value: boolean) => void;
  setProcessing: (value: boolean) => void;
  setStatusMessage: (value: string) => void;
  setActiveStep: (step: number) => void;
}

export function createWizardBleHandlers({
  scanForLifeguard,
  connectToDevice,
  sendWifiCredentials,
  connectedDevice,
  deviceDisplayName,
  isConnected,
  wifiSSID,
  wifiPassword,
  setSkipScan,
  setProcessing,
  setStatusMessage,
  setActiveStep,
}: WizardBleHandlersArgs) {
  const displayName =
    deviceDisplayName || connectedDevice?.name || "unknown device";
  const handleScan = async () => {
    setSkipScan(false);
    setProcessing(true);
    setStatusMessage("Scanning for nearby devices...");
    trackEvent("wizard_started");

    try {
      const device = await scanForLifeguard();
      if (device) {
        trackEvent("device_scanned", {
          device_name: deviceDisplayName || device.name || "unknown",
        });
        setActiveStep(1);
        setStatusMessage('Device found! Click "Connect" to proceed.');
      }
    } catch (error) {
      console.error("Error scanning for devices:", error);
      notify.error(
        "Failed to scan for devices. Please verify that Bluetooth is enabled.",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleConnect = async () => {
    if (!connectedDevice) {
      notify.warning("No device selected. Please scan again.");
      return;
    }

    setProcessing(true);
    setStatusMessage(`Connecting to device ${displayName}...`);

    try {
      await connectToDevice(connectedDevice);
    } catch (error) {
      console.error("Error connecting to device:", error);
      if (!isConnected && connectedDevice.gatt) {
        try {
          setStatusMessage(`Trying to connect again to ${displayName}...`);
          await connectToDevice(connectedDevice);
        } catch (retryError) {
          console.error("Error on retry connection:", retryError);
          notify.error(
            "Failed to connect to the device after multiple attempts. Please try scanning again.",
          );
        }
      } else {
        notify.error(
          "Failed to connect to the device. Please try scanning again.",
        );
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleConfigureWifi = async () => {
    if (!isConnected || !connectedDevice?.gatt) {
      notify.warning("Device not connected. Please connect first.");
      return;
    }
    if (!wifiSSID || !wifiPassword) {
      notify.warning("Please enter the WiFi SSID and password.");
      return;
    }

    setProcessing(true);
    setStatusMessage("Configuring WiFi credentials...");

    try {
      await sendWifiCredentials(
        connectedDevice.gatt,
        UUID_WIFI_SERVICE,
        UUID_CHAR_WIFI_SSID,
        UUID_CHAR_WIFI_PASSWORD,
        UUID_CHAR_WIFI_ENC_METHOD,
        wifiSSID,
        wifiPassword,
      );
      notify.success("WiFi configuration completed!");
      trackEvent("device_wifi_configured");
      setActiveStep(3);
    } catch (error) {
      console.error("Error configuring WiFi:", error);
      notify.error("Failed to configure WiFi on the device.");
    } finally {
      setProcessing(false);
    }
  };

  return { handleScan, handleConnect, handleConfigureWifi };
}
